/**
 * Triova — <product-lightbox>
 * Fullscreen photo viewer for the product gallery. Framework-free web component (Shadow DOM),
 * styled with Triova design tokens (CSS custom properties) with literal fallbacks.
 *
 * Markup:
 *   <product-lightbox id="pdp-lightbox" label="Foto's van DeWalt DWS780" hidden>
 *     <img src="thumb-1.jpg" data-full="large-1.jpg" alt="Vooraanzicht">
 *     <img src="thumb-3.jpg" data-full="large-3.jpg" alt="In gebruik" data-fit="cover">
 *   </product-lightbox>
 *   <button data-lightbox-open="0" data-lightbox-target="pdp-lightbox">Vergroot</button>
 *
 * JS API:  el.open(index) · el.close() · el.go(index) · el.index · el.isOpen
 * Events:  lightbox:open · lightbox:change · lightbox:close   (detail: { index })
 * Attrs:   label (dialog aria-label) · zoom-scale (default 2.2)
 */
(() => {
  if (customElements.get('product-lightbox')) return;

  const svg = (d, w = 20) => `<svg width="${w}" height="${w}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
  const ICON = {
    close: svg('<path d="M6 6l12 12M18 6L6 18"/>'),
    prev: svg('<path d="M19 12H5M11 6l-6 6 6 6"/>'),
    next: svg('<path d="M5 12h14M13 6l6 6-6 6"/>'),
    zoomIn: svg('<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5M11 8v6M8 11h6"/>'),
    zoomOut: svg('<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5M8 11h6"/>')
  };

  const CLAY_REST = 'inset 0 1.5px 0 rgba(140,85,50,.16), inset 0 -3px 0 rgba(90,45,20,.10), 0 2px 6px rgba(58,22,10,.10)';
  const CLAY_HOVER = 'inset 0 1.5px 0 rgba(140,85,50,.16), inset 0 -3px 0 rgba(90,45,20,.10), 0 4px 12px rgba(58,22,10,.14)';
  const CLAY_ACTIVE = 'inset 0 1.5px 0 rgba(140,85,50,.12), inset 0 -3px 0 rgba(90,45,20,.15), 0 1px 1px rgba(58,22,10,.04)';
  const INSET_CARD = 'inset 0 1.5px 0 rgba(140,85,50,.16), inset 0 -3px 0 rgba(90,45,20,.10)';
  const DUR_SHEET = 420;

  // Lubarma: was de laatste invoer een toets? Zie focusDialogStart in utilities.js.
  let lastInputWasKeyboard = false;
  document.addEventListener('keydown', e => {
    if (!['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) lastInputWasKeyboard = true;
  }, true);
  document.addEventListener('pointerdown', () => { lastInputWasKeyboard = false; }, true);

  const CSS = `
:host { position: fixed; inset: 0; z-index: var(--lightbox-z, 1000); display: none;
  font-family: var(--font-body, 'Figtree', system-ui, sans-serif); color: var(--triova-brown-900, #241005); }
:host([open]) { display: block; }
*, *::before, *::after { box-sizing: border-box; }
.scrim { position: absolute; inset: 0; background: var(--overlay-scrim, rgba(36,16,5,.42)); opacity: 0;
  transition: opacity var(--dur-fade, 250ms) ease; }
.panel { position: absolute; inset: 12px; max-width: 1360px; margin: 0 auto; background: #fff;
  border-radius: var(--radius-card, 28px); box-shadow: var(--shadow-drawer, 0 30px 80px -30px rgba(36,16,5,.45));
  display: grid; grid-template-rows: auto minmax(0, 1fr) auto; overflow: hidden; outline: none;
  opacity: 0; transform: translateY(18px) scale(.985);
  transition: opacity var(--dur-fade, 250ms) ease, transform ${DUR_SHEET}ms var(--ease-sheet, cubic-bezier(.2,.8,.2,1)); }
:host([visible]) .scrim { opacity: 1; }
:host([visible]) .panel { opacity: 1; transform: none; }
.bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 14px 0 24px; }
.counter { font-size: 14px; font-weight: 700; color: var(--triova-brown-500, #6E5A4F); font-variant-numeric: tabular-nums; }
.actions { display: flex; gap: 8px; }
.stage { position: relative; margin: 8px 12px; border-radius: var(--radius-media, 20px); background: #fff;
  overflow: hidden; touch-action: none; cursor: zoom-in; user-select: none; -webkit-user-select: none; }
.stage.zoomed { cursor: zoom-out; }
.img { position: absolute; inset: 24px; width: calc(100% - 48px); height: calc(100% - 48px); object-fit: contain;
  transition: transform 320ms var(--ease-sheet, cubic-bezier(.2,.8,.2,1)), opacity 200ms ease; will-change: transform;
  -webkit-user-drag: none; }
.img.swap { opacity: 0; }
.foot { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 0 14px 14px; }
.thumbs { flex: 1; min-width: 0; display: flex; justify-content: center; gap: 10px; padding: 4px; overflow-x: auto; scrollbar-width: none; }
.thumbs::-webkit-scrollbar { display: none; }
.thumb { flex: none; width: 64px; height: 64px; padding: 0; border: 0; border-radius: var(--radius-tile-s, 16px);
  background: #fff; overflow: hidden; cursor: pointer; display: grid; place-items: center;
  box-shadow: var(--inset-card, ${INSET_CARD}); transition: box-shadow 200ms ease; }
.thumb[aria-current="true"] { box-shadow: 0 0 0 2px var(--triova-orange-500, #FA6B36); }
.thumb img { width: 80%; height: 80%; object-fit: contain; pointer-events: none; }
.thumb img.cover { width: 100%; height: 100%; object-fit: cover; }
.dots { flex: 1; display: none; justify-content: center; gap: 2px; }
.dot { width: 28px; height: 28px; padding: 0; border: 0; background: transparent; display: grid; place-items: center; cursor: pointer; }
.dot span { display: block; width: 8px; height: 8px; border-radius: 4px; background: var(--triova-peach-400, #F5DCCD);
  transition: width 200ms ease, background-color 200ms ease; }
.dot[aria-current="true"] span { width: 22px; background: var(--triova-orange-500, #FA6B36); }
.btn { flex: none; width: 44px; height: 44px; padding: 0; border: 1.5px solid var(--color-brand-control-border, #E9DCD3);
  border-radius: 999px; background: #fff; color: inherit; display: grid; place-items: center; cursor: pointer;
  box-shadow: var(--clay-light-rest, ${CLAY_REST});
  transition: transform 200ms ease, box-shadow 200ms ease, background-color 200ms ease; }
.btn:hover { transform: translateY(-1px); box-shadow: var(--clay-light-hover, ${CLAY_HOVER}); }
.btn:active { transform: none; box-shadow: var(--clay-light-active, ${CLAY_ACTIVE}); }
.btn:focus-visible, .thumb:focus-visible, .dot:focus-visible { outline: 2px solid var(--triova-orange-700, #C9501C); outline-offset: 3px; }
.single .foot, .single .counter { visibility: hidden; }
@media (max-width: 760px) {
  .panel { inset: 8px; }
  .bar { padding: 10px 10px 0 18px; }
  .stage { margin: 6px 8px; }
  .img { inset: 8px; width: calc(100% - 16px); height: calc(100% - 16px); }
  .thumbs { display: none; }
  .dots { display: flex; }
  .foot { padding: 0 10px 10px; }
}
@media (prefers-reduced-motion: reduce) {
  .scrim, .panel, .img, .btn, .thumb, .dot span { transition: none !important; }
  .panel { transform: none; }
  .btn:hover { transform: none; }
}`;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ProductLightbox extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.isOpen = false;
      this.zoomed = false;
      this.items = [];
      const root = this.attachShadow({ mode: 'open' });
      root.innerHTML = `<style>${CSS}</style>
<div class="scrim" part="scrim"></div>
<div class="panel" part="panel" role="dialog" aria-modal="true" tabindex="-1">
  <div class="bar">
    <span class="counter" aria-live="polite"></span>
    <div class="actions">
      <button class="btn zoom" type="button" aria-label="Inzoomen" aria-pressed="false">${ICON.zoomIn}</button>
      <button class="btn close" type="button" aria-label="Sluiten">${ICON.close}</button>
    </div>
  </div>
  <div class="stage"><img class="img" alt="" draggable="false"></div>
  <div class="foot">
    <button class="btn prev" type="button" aria-label="Vorige foto">${ICON.prev}</button>
    <div class="thumbs"></div>
    <div class="dots"></div>
    <button class="btn next" type="button" aria-label="Volgende foto">${ICON.next}</button>
  </div>
</div>`;
      const $ = s => root.querySelector(s);
      this.$panel = $('.panel'); this.$stage = $('.stage'); this.$img = $('.img'); this.$counter = $('.counter');
      this.$thumbs = $('.thumbs'); this.$dots = $('.dots'); this.$zoom = $('.zoom'); this.$close = $('.close');

      $('.scrim').addEventListener('click', () => this.close());
      this.$close.addEventListener('click', () => this.close());
      $('.prev').addEventListener('click', () => this.go(this.index - 1));
      $('.next').addEventListener('click', () => this.go(this.index + 1));
      this.$zoom.addEventListener('click', () => this.setZoom(!this.zoomed));
      this.$thumbs.addEventListener('click', e => { const b = e.target.closest('[data-i]'); if (b) this.go(+b.dataset.i); });
      this.$dots.addEventListener('click', e => { const b = e.target.closest('[data-i]'); if (b) this.go(+b.dataset.i); });

      this.$stage.addEventListener('pointerdown', this.onDown);
      this.$stage.addEventListener('pointermove', this.onMove);
      this.$stage.addEventListener('pointerup', this.onUp);
      this.$stage.addEventListener('pointercancel', () => { this.ptr = null; });
    }

    connectedCallback() { document.addEventListener('click', this.onDocClick); }
    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
      if (this.isOpen) this.unlockScroll();
    }

    get zoomScale() { return parseFloat(this.getAttribute('zoom-scale')) || 2.2; }

    readItems() {
      return Array.from(this.querySelectorAll(':scope > img')).map(img => ({
        src: img.dataset.full || img.currentSrc || img.src,
        thumb: img.currentSrc || img.src,
        alt: img.alt || '',
        cover: img.dataset.fit === 'cover'
      }));
    }

    open(i = 0) {
      this.items = this.readItems();
      if (!this.items.length) return;
      this.$panel.setAttribute('aria-label', this.getAttribute('label') || "Productfoto's");
      this.$panel.classList.toggle('single', this.items.length < 2);
      this.$thumbs.innerHTML = this.items.map((it, n) =>
        `<button class="thumb" type="button" data-i="${n}" aria-label="Foto ${n + 1}"><img src="${it.thumb}" alt=""${it.cover ? ' class="cover"' : ''}></button>`).join('');
      this.$dots.innerHTML = this.items.map((_, n) =>
        `<button class="dot" type="button" data-i="${n}" aria-label="Foto ${n + 1}"><span></span></button>`).join('');
      this.lastFocus = document.activeElement;
      this.go(i, { silent: true, instant: true });
      if (!this.isOpen) {
        this.isOpen = true;
        clearTimeout(this.closeTimer);
        this.removeAttribute('hidden');
        this.setAttribute('open', '');
        this.lockScroll();
        requestAnimationFrame(() => requestAnimationFrame(() => this.setAttribute('visible', '')));
        document.addEventListener('keydown', this.onKey);
        // Lubarma: alleen na een toets op de sluitknop; na een klik of tik op
        // het paneel, zodat de knop geen focusring krijgt.
        (lastInputWasKeyboard ? this.$close : this.$panel).focus({ preventScroll: true });
        this.emit('lightbox:open');
      }
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.setZoom(false);
      this.removeAttribute('visible');
      document.removeEventListener('keydown', this.onKey);
      this.closeTimer = setTimeout(() => {
        this.removeAttribute('open');
        this.unlockScroll();
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      }, reducedMotion() ? 0 : DUR_SHEET);
      this.emit('lightbox:close');
    }

    go(i, { silent = false, instant = false } = {}) {
      const n = this.items.length;
      if (!n) return;
      this.index = ((i % n) + n) % n;
      const it = this.items[this.index];
      this.setZoom(false);
      const apply = () => { this.$img.src = it.src; this.$img.alt = it.alt; this.$img.classList.remove('swap'); };
      if (instant || reducedMotion()) apply();
      else { this.$img.classList.add('swap'); clearTimeout(this.swapTimer); this.swapTimer = setTimeout(apply, 140); }
      this.$counter.textContent = `${this.index + 1} / ${n}`;
      this.$thumbs.querySelectorAll('.thumb').forEach((b, k) => b.setAttribute('aria-current', String(k === this.index)));
      this.$dots.querySelectorAll('.dot').forEach((b, k) => b.setAttribute('aria-current', String(k === this.index)));
      const active = this.$thumbs.children[this.index];
      if (active) this.$thumbs.scrollTo({ left: active.offsetLeft - this.$thumbs.clientWidth / 2 + 32, behavior: 'smooth' });
      [1, -1].forEach(d => { const nb = this.items[(this.index + d + n) % n]; if (nb) new Image().src = nb.src; });
      if (!silent) this.emit('lightbox:change');
    }

    setZoom(on, clientX, clientY) {
      this.zoomed = on;
      this.$stage.classList.toggle('zoomed', on);
      if (on && clientX != null) this.setOrigin(clientX, clientY);
      if (!on) this.$img.style.transformOrigin = '50% 50%';
      this.$img.style.transform = on ? `scale(${this.zoomScale})` : '';
      this.$zoom.setAttribute('aria-pressed', String(on));
      this.$zoom.setAttribute('aria-label', on ? 'Uitzoomen' : 'Inzoomen');
      this.$zoom.innerHTML = on ? ICON.zoomOut : ICON.zoomIn;
    }

    setOrigin(clientX, clientY) {
      // Use the untransformed layout box (offset*), so panning stays stable while scaled.
      const s = this.$stage.getBoundingClientRect();
      const left = s.left + this.$img.offsetLeft, top = s.top + this.$img.offsetTop;
      const x = Math.min(100, Math.max(0, ((clientX - left) / this.$img.offsetWidth) * 100));
      const y = Math.min(100, Math.max(0, ((clientY - top) / this.$img.offsetHeight) * 100));
      this.$img.style.transformOrigin = `${x}% ${y}%`;
    }

    onDown = e => {
      this.ptr = { id: e.pointerId, x: e.clientX, y: e.clientY, type: e.pointerType };
      this.$stage.setPointerCapture(e.pointerId);
    };

    onMove = e => {
      if (!this.zoomed) return;
      if (e.pointerType === 'mouse' || (this.ptr && this.ptr.id === e.pointerId)) {
        this.$img.style.transition = 'opacity 200ms ease';
        this.setOrigin(e.clientX, e.clientY);
      }
    };

    onUp = e => {
      const p = this.ptr; this.ptr = null;
      this.$img.style.transition = '';
      if (!p || p.id !== e.pointerId) return;
      const dx = e.clientX - p.x, dy = e.clientY - p.y;
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) { this.setZoom(!this.zoomed, e.clientX, e.clientY); return; }
      if (!this.zoomed && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
    };

    onKey = e => {
      if (e.key === 'Escape') { e.preventDefault(); this.zoomed ? this.setZoom(false) : this.close(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      else if (e.key === 'Tab') {
        const f = Array.from(this.shadowRoot.querySelectorAll('button')).filter(b => !b.disabled && b.offsetParent !== null);
        if (!f.length) return;
        const cur = this.shadowRoot.activeElement, i = f.indexOf(cur);
        e.preventDefault();
        // Staat de focus op het paneel (i = -1), dan gaat Tab naar de eerste
        // en Shift+Tab naar de laatste knop.
        if (i === -1) { f[e.shiftKey ? f.length - 1 : 0].focus(); return; }
        f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      }
    };

    onDocClick = e => {
      const t = e.target.closest && e.target.closest('[data-lightbox-open]');
      if (!t) return;
      const target = t.getAttribute('data-lightbox-target');
      if (target && target !== this.id) return;
      e.preventDefault();
      this.open(parseInt(t.getAttribute('data-lightbox-open'), 10) || 0);
    };

    lockScroll() {
      const de = document.documentElement, sb = window.innerWidth - de.clientWidth;
      this.prevOverflow = de.style.overflow; this.prevPad = document.body.style.paddingRight;
      de.style.overflow = 'hidden';
      if (sb > 0) document.body.style.paddingRight = sb + 'px';
    }
    unlockScroll() {
      document.documentElement.style.overflow = this.prevOverflow || '';
      document.body.style.paddingRight = this.prevPad || '';
    }

    emit(type) { this.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { index: this.index } })); }
  }

  customElements.define('product-lightbox', ProductLightbox);
})();
